import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Navbar from '../components/Navbar';

function Discovery() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [userIdentities, setUserIdentities] = useState([]);
  const [identityCategories, setIdentityCategories] = useState([]);
  const [selectedTab, setSelectedTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [trustLimits, setTrustLimits] = useState(null);
  const [introSuggestions, setIntroSuggestions] = useState([]);

  useEffect(() => {
    loadTrustLimits();
    loadUserIdentities();
    fetchMatches();
  }, []);

  const loadTrustLimits = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/trust-score/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrustLimits(response.data.limits);
    } catch (error) {
      console.error('Error loading trust limits:', error);
    }
  };

  const loadUserIdentities = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/identities/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserIdentities(response.data.identities);
      
      // Get unique categories
      const categories = [...new Set(response.data.identities.map(i => ({
        id: i.category_id,
        name: i.category_name,
        display_name: i.category_display_name,
        icon: i.category_icon,
        color: i.category_color
      })))];
      
      // Remove duplicates by category_id
      const uniqueCategories = categories.filter((cat, index, self) =>
        index === self.findIndex((c) => c.id === cat.id)
      );
      
      setIdentityCategories(uniqueCategories);
    } catch (error) {
      console.error('Error loading identities:', error);
    }
  };

  const fetchMatches = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/discovery/matches', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Discovery matches:', response.data.matches);
      setMatches(response.data.matches);
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (matchUserId) => {
    setSending(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // Check connection request limits
      const limitsResponse = await axios.get('/api/trust-score/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (limitsResponse.data.limits.requestsRemaining <= 0) {
        setMessage(`⏰ Daily connection request limit reached (${limitsResponse.data.limits.maxRequestsPerDay}/${limitsResponse.data.limits.maxRequestsPerDay}). Try again tomorrow or increase your trust score.`);
        setSending(false);
        return;
      }
      
      // Send connection request
      await axios.post('/api/connections/request', {
        toUserId: matchUserId,
        message: requestMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Track the request for limits
      await axios.post('/api/trust-score/track-request', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage('✅ Connection request sent!');
      setSelectedMatch(null);
      setRequestMessage('');
      setIntroSuggestions([]);
      
      // Reload limits
      await loadTrustLimits();
      
      // Remove from matches
      setMatches(matches.filter(m => m.user_id !== matchUserId));
    } catch (error) {
      const errData = error.response?.data;
      const errMsg = errData?.error || errData?.errors?.[0]?.msg || 'Failed to send request';
      console.error('Connection request failed:', errData);
      setMessage(`❌ ${errMsg}`);
      alert(errMsg);
    } finally {
      setSending(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getFilteredMatches = () => {
    if (selectedTab === 'all') {
      return matches;
    }
    
    // Filter by selected category
    return matches.filter(match => {
      if (!match.shared_categories) return false;
      return match.shared_categories.some(cat => 
        identityCategories.find(ic => ic.display_name === cat && ic.name === selectedTab)
      );
    });
  };

  const filteredMatches = getFilteredMatches();

  return (
    <div>
      <Navbar />

      <div className="container">
        <div className="card">
          <h2>Find your people</h2>
          <p style={{ color: '#666' }}>
            Discover people who share your identities. Everyone's anonymous until you connect
          </p>
          
          {userIdentities.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <strong>Your Identities:</strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {userIdentities.map(identity => (
                  <span key={identity.user_identity_id} style={{
                    padding: '4px 12px',
                    backgroundColor: '#e7f3ff',
                    border: '1px solid #b3d9ff',
                    borderRadius: '16px',
                    fontSize: '13px'
                  }}>
                    {identity.identity_name}
                    {identity.is_primary && ' ⭐'}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Trust Score Limits - Only Connection Requests */}
          {trustLimits && (
            <div style={{ 
              marginTop: '20px', 
              padding: '16px', 
              backgroundColor: trustLimits.requestsRemaining === 0 ? '#fff3cd' : '#e7f3ff',
              border: `1px solid ${trustLimits.requestsRemaining === 0 ? '#ffc107' : '#b3d9ff'}`,
              borderRadius: '8px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <strong>Today's Connection Requests</strong>
                <span style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold',
                  color: trustLimits.requestsRemaining === 0 ? '#856404' : '#0066cc'
                }}>
                  {trustLimits.requestsRemaining} / {trustLimits.maxRequestsPerDay}
                </span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                backgroundColor: '#f0f0f0', 
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '8px'
              }}>
                <div style={{ 
                  width: `${(trustLimits.requestsSentToday / trustLimits.maxRequestsPerDay) * 100}%`,
                  height: '100%',
                  backgroundColor: trustLimits.requestsRemaining === 0 ? '#ffc107' : '#4CAF50',
                  transition: 'width 0.3s'
                }} />
              </div>
              {trustLimits.requestsRemaining === 0 ? (
                <p style={{ margin: 0, fontSize: '13px', color: '#856404' }}>
                  ⏰ You've reached your daily limit. Come back tomorrow or <a href="/profile" style={{ color: '#856404', textDecoration: 'underline' }}>increase your trust score</a> for more requests.
                </p>
              ) : (
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                  {trustLimits.requestsRemaining} request{trustLimits.requestsRemaining === 1 ? '' : 's'} remaining today. Limits reset at midnight.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        {identityCategories.length > 0 && (
          <div className="card" style={{ padding: '0' }}>
            <div style={{ 
              display: 'flex', 
              overflowX: 'auto',
              borderBottom: '2px solid #e0e0e0'
            }}>
              <button
                onClick={() => setSelectedTab('all')}
                style={{
                  padding: '16px 24px',
                  border: 'none',
                  background: selectedTab === 'all' ? '#007bff' : 'transparent',
                  color: selectedTab === 'all' ? 'white' : '#666',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: selectedTab === 'all' ? 'bold' : 'normal',
                  borderBottom: selectedTab === 'all' ? '3px solid #007bff' : 'none',
                  whiteSpace: 'nowrap'
                }}
              >
                All ({matches.length})
              </button>
              
              {identityCategories.map(category => {
                const categoryMatches = matches.filter(m => 
                  m.shared_categories && m.shared_categories.includes(category.display_name)
                );
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedTab(category.name)}
                    style={{
                      padding: '16px 24px',
                      border: 'none',
                      background: selectedTab === category.name ? category.color : 'transparent',
                      color: selectedTab === category.name ? 'white' : '#666',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: selectedTab === category.name ? 'bold' : 'normal',
                      borderBottom: selectedTab === category.name ? `3px solid ${category.color}` : 'none',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {category.icon} {category.display_name} ({categoryMatches.length})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {message && (
          <div className={message.includes('sent') ? 'card' : 'card'} style={{
            backgroundColor: message.includes('sent') ? '#d4edda' : '#f8d7da',
            color: message.includes('sent') ? '#155724' : '#721c24',
            border: `1px solid ${message.includes('sent') ? '#c3e6cb' : '#f5c6cb'}`
          }}>
            {message}
          </div>
        )}

        {loading ? (
          <div className="card">Loading matches...</div>
        ) : filteredMatches.length === 0 ? (
          <div className="card">
            <p>No matches yet {selectedTab !== 'all' ? `in ${identityCategories.find(c => c.name === selectedTab)?.display_name}` : 'in your area'} 🌱</p>
            <p style={{ color: '#666', fontSize: '14px' }}>
              {selectedTab !== 'all' ? 'Try a different tab or ' : ''}Invite friends to join Belong!
            </p>
          </div>
        ) : (
          <div>
            <div className="card">
              <h3>Found {filteredMatches.length} {filteredMatches.length === 1 ? 'person' : 'people'} 
                {selectedTab !== 'all' && ` in ${identityCategories.find(c => c.name === selectedTab)?.display_name}`}
              </h3>
            </div>

            {filteredMatches.map((match) => (
              <div key={match.user_id} className="card">
                <div style={{ display: 'flex', alignItems: 'start', gap: '20px' }}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '50%', 
                    backgroundColor: '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px'
                  }}>
                    👤
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <h3 style={{ margin: 0 }}>Anonymous User</h3>
                      {match.shared_identity_count > 1 && (
                        <span style={{
                          backgroundColor: '#ffd700',
                          color: '#000',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ⭐ {match.shared_identity_count} shared identities
                        </span>
                      )}
                      {match.is_student && match.college && user?.is_student && user?.college === match.college && (
                        <span style={{
                          backgroundColor: '#0066cc',
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          🎓 Same college!
                        </span>
                      )}
                    </div>
                    
                    {/* Shared Identities */}
                    {match.shared_identities && match.shared_identities.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <p style={{ color: '#28a745', fontWeight: 'bold', margin: '4px 0' }}>
                          ✓ Shares: {match.shared_identities.join(', ')}
                        </p>
                      </div>
                    )}
                    
                    <div style={{ marginTop: '12px' }}>
                      {match.current_city && (
                        <p>📍 {match.current_city}, {match.current_state}</p>
                      )}
                      {match.hometown_city && (
                        <p>🏠 From {match.hometown_city}, {match.hometown_state}</p>
                      )}
                      {match.profession && (
                        <p>💼 {match.profession}</p>
                      )}
                      {match.age && (
                        <p>👤 {match.age} years old</p>
                      )}
                      {match.interests && match.interests.length > 0 && (
                        <p>⭐ {match.interests.map(i => i.replace(/_/g, ' ')).join(', ')}</p>
                      )}
                      <p style={{ color: '#999', fontSize: '14px' }}>
                        Member since {new Date(match.member_since).toLocaleDateString()}
                      </p>
                    </div>

                    {selectedMatch === match.user_id ? (
                      <div style={{ marginTop: '16px' }}>
                        {introSuggestions.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <p style={{ fontSize: '13px', color: '#666', marginBottom: '6px' }}>💡 Suggested intros:</p>
                            {introSuggestions.map((s, i) => (
                              <button key={i} onClick={() => setRequestMessage(s)} style={{
                                display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                                marginBottom: '6px', background: '#f5f3ff', border: '1px solid #e0d4f5',
                                borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#4c1d95'
                              }}>
                                {s}
                              </button>
                            ))}
                          </div>
                        )}
                        <textarea
                          placeholder="Tell them why you want to connect (20-300 chars)..."
                          value={requestMessage}
                          onChange={(e) => setRequestMessage(e.target.value)}
                          rows="3"
                          maxLength={300}
                          style={{ width: '100%', padding: '8px', marginBottom: '4px' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px' }}>
                          <span style={{ color: requestMessage.length < 20 ? '#e74c3c' : '#28a745' }}>
                            {requestMessage.length}/300 {requestMessage.length < 20 ? `(${20 - requestMessage.length} more needed)` : '✓'}
                          </span>
                          <span style={{ color: '#999' }}>🔒 Don't share phone, email, or social handles</span>
                        </div>
                        <button 
                          onClick={() => handleSendRequest(match.user_id)}
                          className="btn btn-primary"
                          disabled={sending || requestMessage.length < 20}
                          style={{ marginRight: '8px' }}
                        >
                          {sending ? 'Sending...' : 'Send'}
                        </button>
                        <button 
                          onClick={() => setSelectedMatch(null)}
                          className="btn btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button 
                        onClick={async () => {
                          setSelectedMatch(match.user_id);
                          setRequestMessage('');
                          try {
                            const token = localStorage.getItem('accessToken');
                            const res = await axios.get(`/api/connections/intro-suggestions/${match.user_id}`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            setIntroSuggestions(res.data.suggestions || []);
                          } catch(e) { setIntroSuggestions([]); }
                        }}
                        className="btn btn-primary"
                      >
                        Say hi
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Discovery;

